import React, { useRef, useState, useEffect } from 'react';
import { Calendar, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface DateRangeFilterProps {
    startDate: string | null;
    endDate: string | null;
    onDateChange: (start: string | null, end: string | null) => void;
    placeholderStart?: string;
    placeholderEnd?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
    startDate,
    endDate,
    onDateChange,
    placeholderStart = 'Start',
    placeholderEnd = 'End'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Sync temp state when props change externally
    useEffect(() => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    }, [startDate, endDate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setTempStartDate(null);
        setTempEndDate(null);
        onDateChange(null, null);
        setIsOpen(false);
    };

    const handleApply = () => {
        onDateChange(tempStartDate, tempEndDate);
        setIsOpen(false);
    };

    const displayText = startDate || endDate
        ? `${startDate || '...'}  →  ${endDate || '...'}`
        : 'Filter Date';

    const containerBaseClass = "flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 cursor-pointer relative";

    return (
        <>
            <style>{`
                input.date-range-input {
                    font-weight: 600;
                    color: #111827;
                    letter-spacing: 0.01em;
                }

                input.date-range-input::-webkit-datetime-edit,
                input.date-range-input::-webkit-datetime-edit-text,
                input.date-range-input::-webkit-datetime-edit-month-field,
                input.date-range-input::-webkit-datetime-edit-day-field,
                input.date-range-input::-webkit-datetime-edit-year-field {
                    font-weight: 600;
                    color: #111827;
                    -webkit-text-fill-color: #111827;
                }

                input.date-range-input::-moz-placeholder {
                    font-weight: 600;
                    color: #111827;
                    opacity: 1;
                }

                input.date-range-input::placeholder {
                    font-weight: 600;
                    color: #111827;
                    opacity: 1;
                }
            `}</style>
            <div className="relative" ref={dropdownRef}>
            <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsOpen(!isOpen)}
                className={containerBaseClass}
                style={{
                    background: 'var(--color-secondary-orange)',
                    display: 'flex',
                    alignItems: 'center',
                }}
                title="Filter by date range"
            >
                <div className="flex items-center gap-2 mr-1">
                    <Calendar size={12} strokeWidth={2.5} className="drop-shadow-sm" />
                    <span className="date-range-trigger-label uppercase tracking-wide">{displayText}</span>
                </div>

                <div className="flex items-center gap-1">
                    {(startDate || endDate) && (
                        <div
                            onClick={handleClear}
                            className="p-0.5 rounded hover:bg-white/20"
                            title="Clear filter"
                        >
                            <X size={10} />
                        </div>
                    )}
                    <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.95 }}
                        className="absolute top-full left-0 mt-2 z-50 p-4 rounded-lg shadow-xl"
                        style={{
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            minWidth: '280px',
                            color: '#374151'
                        }}
                    >
                        <div className="flex flex-col gap-3">
                            <h4 className="text-xs text-gray-900 uppercase tracking-wider mb-1" style={{ fontWeight: '400 !important' }}>Select Range</h4>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-900" style={{ fontWeight: '400 !important' }}>Start Date</label>
                                <input
                                    className="date-range-input w-full border border-gray-300 rounded px-2 py-1.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-[#0084CF]"
                                    type="date"
                                    value={tempStartDate || ''}
                                    onChange={(e) => setTempStartDate(e.target.value || null)}
                                    style={{ color: '#111827' }}
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-900" style={{ fontWeight: '400 !important' }}>End Date</label>
                                <input
                                    className="date-range-input w-full border border-gray-300 rounded px-2 py-1.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-[#0084CF]"
                                    type="date"
                                    value={tempEndDate || ''}
                                    onChange={(e) => setTempEndDate(e.target.value || null)}
                                    style={{ color: '#111827' }}
                                />
                            </div>

                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                                <button
                                    onClick={(e) => handleClear(e)}
                                    className="text-xs text-red-500 hover:text-red-700 font-bold px-2 py-1 hover:bg-red-50 rounded"
                                >
                                    Reset
                                </button>
                                <button
                                    onClick={handleApply}
                                    className="text-xs bg-[#0066A3] hover:bg-[#1C4D88] text-white font-bold px-3 py-1 rounded transition-colors shadow-sm"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            </div>
        </>
    );
};
